import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Globe, Truck, Clock, Shield, ArrowRight, CheckCircle2, PackageCheck } from 'lucide-react';
import axios from 'axios';
import Reveal from '../components/Reveal';
import CountUp from '../components/CountUp';
import TrustBar from '../components/TrustBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [companyInfo, setCompanyInfo] = useState({
    hero_title: 'Delivering Your World, On Time',
    hero_subtitle: 'Expert international shipping and last-mile delivery solutions that connect your business to the world. Fast, reliable, and secure.',
  });

  const [stats, setStats] = useState([
    { number: '50+', label: 'Countries Served' },
    { number: '10K+', label: 'Shipments Monthly' },
    { number: '99.8%', label: 'On-Time Delivery' },
    { number: '24/7', label: 'Customer Support' },
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyRes, statsRes] = await Promise.all([
          axios.get(`${API}/content/company-info`),
          axios.get(`${API}/content/stats`)
        ]);
        setCompanyInfo(companyRes.data);
        setStats(statsRes.data.stats);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  const services = [
    {
      icon: Globe,
      title: 'International Shipping',
      description: 'Seamless global freight solutions connecting your business to worldwide markets.',
    },
    {
      icon: Truck,
      title: 'Last-Mile Delivery',
      description: 'Fast, reliable delivery services ensuring your packages reach their final destination.',
    },
    {
      icon: Clock,
      title: 'Express Services',
      description: 'Time-critical shipments handled with priority and precision.',
    },
    {
      icon: Shield,
      title: 'Secure Handling',
      description: 'Advanced tracking and security measures for complete peace of mind.',
    },
  ];

  const features = [
    'Real-time tracking and updates',
    'Customs clearance assistance',
    'Flexible shipping options',
    'Dedicated account management',
    'Competitive pricing',
    'Insurance coverage available',
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-gray-50 py-20 lg:py-32">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -right-16 w-96 h-96 bg-emerald-200/40 rounded-full blur-3xl animate-blob" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 w-80 h-80 bg-red-200/30 rounded-full blur-3xl animate-blob" style={{ animationDelay: '4s' }} aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600" />
                </span>
                Global Logistics Excellence
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {companyInfo.hero_title}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {companyInfo.hero_subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="group bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all active:scale-95 w-full sm:w-auto">
                    Get a Quote
                    <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={20} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-lg transition-all active:scale-95 w-full sm:w-auto">
                    Our Services
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-50 p-8 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=800&q=80"
                  alt="Logistics"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              {/* Floating trust badge */}
              <div className="hidden sm:flex items-center gap-3 absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl px-5 py-4 animate-float">
                <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <PackageCheck className="text-emerald-600" size={24} />
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900 leading-none">99.8%</div>
                  <div className="text-xs text-gray-500 mt-1">On-time delivery</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / partner logos (hidden until logos are added — see TrustBar.jsx) */}
      <TrustBar heading="Trusted by partners worldwide" />

      {/* Stats Section */}
      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <CountUp
                  value={stat.number}
                  className="block text-4xl lg:text-5xl font-bold text-emerald-400 mb-2"
                />
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Logistics Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From international freight to your customer's doorstep, we handle it all.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Reveal key={index} delay={index * 100}>
                  <Card className="h-full border-2 hover:border-emerald-500 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 group">
                    <CardContent className="p-6 space-y-4">
                      <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center transition-colors group-hover:bg-emerald-500">
                        <Icon className="text-emerald-600 transition-colors group-hover:text-white" size={28} />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">{service.title}</h3>
                      <p className="text-gray-600">{service.description}</p>
                    </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Choose International Quality Group?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We combine cutting-edge technology with decades of logistics expertise to deliver exceptional service every time.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="text-emerald-600 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={120} className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1494412651409-8963ce7935a7?w=800&q=80"
                alt="Warehouse"
                className="w-full transition-transform duration-500 hover:scale-105"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden py-20 bg-emerald-600">
        <div className="pointer-events-none absolute -top-20 -right-10 w-80 h-80 bg-emerald-400/40 rounded-full blur-3xl animate-blob" aria-hidden="true" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Ship Smarter?
          </h2>
          <p className="text-xl text-emerald-50 mb-8">
            Get a personalized quote for your shipping needs in minutes. Our team is ready to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="group bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all active:scale-95 w-full sm:w-auto">
                Request a Quote
                <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={20} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-emerald-700 px-8 py-6 text-lg transition-all active:scale-95 w-full sm:w-auto">
                Contact Us
              </Button>
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;